import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { UserSelectors } from '../../../states/user/user-selector';
import { PaymentService } from '../payment/payment.service';
import { PackagesService } from '../packages/packages.service';
import { AuthService } from '../../account/auth.service';
import { StudentDirectApplicationService } from '../../student-direct-application/student-direct-application.service';
import { CreatePayNowRequest } from '../../../models/payment/createPayNowRequest';
import { PaymentGatewayResponse } from '../../../models/payment/paymentGatewayResponse.model';

/**
 * Centralises the Direct Apply "pay now" initiation so both the Review step and the
 * Payment step's retry use identical request-building logic (amount, colleges, return URL).
 */
@Injectable({ providedIn: 'root' })
export class DirectApplyCheckoutService {
  private readonly PACKAGE_NAME = 'Institute Direct Application';
  private readonly GST_RATE = 0.18;
  private readonly DEFAULT_PER_COLLEGE_AMOUNT = 1000;

  constructor(
    private store: Store,
    private paymentService: PaymentService,
    private packagesService: PackagesService,
    private authService: AuthService,
    private studentDirectApplicationService: StudentDirectApplicationService
  ) { }

  /**
   * Fetches the current student, the Direct Apply package and the selected colleges,
   * then submits a pay-now request to the gateway.
   * @param sourceUrl Return URL the gateway redirects the user back to after payment.
   */
  initiatePayment(sourceUrl: string): Observable<PaymentGatewayResponse> {
    return this.store.select(UserSelectors.getUser).pipe(
      filter(user => !!user?.id),
      take(1),
      switchMap(user =>
        forkJoin({
          userDetails: this.authService.getUserByid(user.id),
          packageDetails: this.packagesService.getPackageBySystemAssignedName(this.PACKAGE_NAME),
          colleges: this.studentDirectApplicationService.getByStudentId({ studentId: user.id }).pipe(catchError(() => of(null)))
        })
      ),
      switchMap(({ userDetails, packageDetails, colleges }) => {
        const collegeIds = this.extractCollegeIds(colleges);
        const perCollege = packageDetails?.amount || this.DEFAULT_PER_COLLEGE_AMOUNT;
        const baseAmount = perCollege * collegeIds.length;
        const totalAmount = +(baseAmount * (1 + this.GST_RATE)).toFixed(2);

        const fullName = `${userDetails.firstName} ${userDetails.lastName || ''}`.trim();
        const cleanPhone = userDetails.phoneNumber
          ? userDetails.phoneNumber.replace(/\D/g, '').substring(0, 10)
          : '0000000000';

        const request: CreatePayNowRequest = {
          name: fullName,
          userEmail: userDetails.email,
          phoneNumber: cleanPhone,
          amount: totalAmount,
          transactionType: 'SALE',
          sourceUrl,
          packageId: packageDetails?.id,
          billingAddress: {
            userId: userDetails.id,
            billAddress: 'N/A'
          },
          metaData: collegeIds
        };

        return this.paymentService.payNow(request);
      })
    );
  }

  /** Normalises the various shapes the student-direct-application API can return into a list of ids. */
  private extractCollegeIds(response: any): number[] {
    let rawItems: any[] = [];
    if (Array.isArray(response)) {
      rawItems = response;
    } else if (response?.result) {
      rawItems = Array.isArray(response.result) ? response.result : [response.result];
    } else if (response?.instituteId) {
      rawItems = [response];
    }
    // DTO changed but id remains in StudentDirectApplicationResponse
    return rawItems.map(item => item.id ?? 0).filter((id: number) => id > 0);
  }
}
